/**
 * UEM Navigation – Workflow-based structure + Platform Admin command center.
 */

export interface NavItemConfig {
  id: string;
  label: string;
  icon: string;
}

export const UEM_NAV_ITEMS: NavItemConfig[] = [
  { id: 'overview', label: 'Overview', icon: 'Server' },
  { id: 'devices', label: 'Devices', icon: 'Laptop' },
  { id: 'asset-management', label: 'Asset Directory', icon: 'Package' },
  { id: 'policies-compliance', label: 'Policies & Compliance', icon: 'Shield' },
  { id: 'app-deployment', label: 'Apps & Deployment', icon: 'Box' },
  { id: 'remote-execution', label: 'Remote Execution', icon: 'Terminal' },
  { id: 'patch-management', label: 'Patch Management', icon: 'ShieldCheck' },
  { id: 'inventory', label: 'Inventory', icon: 'BarChart' },
  { id: 'license-management', label: 'License Management', icon: 'Key' },
  { id: 'software-metering', label: 'Software Metering', icon: 'Activity' },
  { id: 'analytics', label: 'Analytics', icon: 'FileText' },
  { id: 'buntu-companion', label: 'Buntu Companion', icon: 'MonitorSmartphone' },
  { id: 'platform-admin', label: 'Platform Admin', icon: 'Building2' },
  { id: 'client-admin', label: 'Client Admin', icon: 'Users' },
  { id: 'settings', label: 'Settings', icon: 'Settings' },
];

/**
 * Platform SaaS modules (TenantModule.module_key).
 * Nav visibility is always shown for tenant users; access requires the mapped module.
 * Keys must match backend DEFAULT_MODULES / Platform Admin Modules toggles.
 * When adding tenant Hub modules or Policies tabs, also update:
 *   app/permission_catalog_data.py PAGE_KEYS + NAV_TO_PAGE
 *   app/tenant_rbac.py DEFAULT_MODULES (if new module)
 * then run: python scripts/seed_tenant_ui_access.py
 */
export const PLATFORM_MODULE_CATALOG: { key: string; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard / Overview' },
  { key: 'devices', label: 'Devices' },
  { key: 'policies', label: 'Policies & Compliance' },
  { key: 'app-deployment', label: 'Apps & Deployment' },
  { key: 'remote-execution', label: 'Remote Execution' },
  { key: 'patch-management', label: 'Patch Management' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'license-management', label: 'License Management' },
  { key: 'asset_management', label: 'Asset Directory' },
  { key: 'software_metering', label: 'Software Metering' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'companion', label: 'Buntu Companion' },
  { key: 'client_admin', label: 'Client Admin' },
  { key: 'settings', label: 'Settings' },
  { key: 'enrollment', label: 'Enrollment' },
  { key: 'android', label: 'Android' },
  { key: 'ios', label: 'iOS' },
];

/** Nav tab id → platform TenantModule key. Every tenant UI nav item maps here. */
export const NAV_MODULE_BY_ID: Record<string, string> = {
  overview: 'dashboard',
  devices: 'devices',
  'policies-compliance': 'policies',
  'app-deployment': 'app-deployment',
  'remote-execution': 'remote-execution',
  'patch-management': 'patch-management',
  inventory: 'inventory',
  'license-management': 'license-management',
  'asset-management': 'asset_management',
  'software-metering': 'software_metering',
  analytics: 'analytics',
  'buntu-companion': 'companion',
  'client-admin': 'client_admin',
  'user-management': 'client_admin',
  settings: 'settings',
};

export function isPlatformModuleEnabled(
  navId: string,
  enabledModules: string[] | null | undefined,
  bypass = false
): boolean {
  if (bypass) return true;
  const mod = NAV_MODULE_BY_ID[navId];
  if (!mod) return true;
  if (!enabledModules) return false;
  return enabledModules.includes(mod);
}

/** Nested Policies & Compliance children (exact product names). Keep in sync with PAGE_KEYS. */
export const POLICIES_COMPLIANCE_NAV_ITEMS: NavItemConfig[] = [
  { id: 'workstation-policies', label: 'Workstation Policies', icon: 'Laptop' },
  { id: 'mobile-devices', label: 'Mobile Devices', icon: 'Smartphone' },
  { id: 'server-baselines', label: 'Server Compliance Baselines', icon: 'Server' },
  { id: 'policy-deployment-status', label: 'Deployment Status', icon: 'Activity' },
  { id: 'compliance-results', label: 'Compliance Results', icon: 'Check' },
  { id: 'device-classification', label: 'Device Classification', icon: 'Laptop' },
  { id: 'compliance-exceptions', label: 'Exceptions & Approvals', icon: 'FileText' },
];

/** Nested Platform Admin command-center pages (PLATFORM_ADMIN default shell). */
export const PLATFORM_NAV_ITEMS: NavItemConfig[] = [
  { id: 'platform-dashboard', label: 'Dashboard', icon: 'Server' },
  { id: 'platform-clients', label: 'Clients / Tenants', icon: 'Building2' },
  { id: 'platform-buntu', label: 'Buntu Broadcasts', icon: 'MessageSquare' },
  { id: 'platform-companion', label: 'Buntu Companion', icon: 'MonitorSmartphone' },
  { id: 'platform-subscriptions', label: 'Subscriptions', icon: 'FileText' },
  { id: 'platform-billing', label: 'Billing', icon: 'Key' },
  { id: 'platform-license', label: 'License & Usage', icon: 'BarChart' },
  { id: 'platform-entitlements', label: 'Module & OS Entitlements', icon: 'ShieldCheck' },
  { id: 'platform-software-metering', label: 'Software Metering', icon: 'Activity' },
  { id: 'platform-users', label: 'Platform Users', icon: 'Users' },
  { id: 'platform-audit', label: 'Audit Logs', icon: 'FileText' },
  { id: 'platform-health', label: 'System Health', icon: 'Activity' },
  { id: 'platform-settings', label: 'Platform Settings', icon: 'Settings' },
];

export const PLATFORM_ROLES = new Set([
  'PLATFORM_ADMIN',
  'PLATFORM_SUPPORT',
  'PLATFORM_BILLING',
  'PLATFORM_VIEWER',
]);

/**
 * Roles allowed to see/render each platform page.
 * Clients + Buntu match admin-only backend gates (platform_tenants / platform_buntu).
 * Writes on other pages may still be admin-only even when SUPPORT/VIEWER can view.
 */
export const PLATFORM_PAGE_ROLES: Record<string, string[]> = {
  'platform-dashboard': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_VIEWER'],
  'platform-clients': ['PLATFORM_ADMIN'],
  'platform-buntu': ['PLATFORM_ADMIN'],
  'platform-companion': ['PLATFORM_ADMIN'],
  'platform-subscriptions': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_BILLING', 'PLATFORM_VIEWER'],
  'platform-billing': ['PLATFORM_ADMIN', 'PLATFORM_BILLING', 'PLATFORM_SUPPORT'],
  'platform-license': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_VIEWER'],
  'platform-entitlements': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_VIEWER'],
  'platform-software-metering': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_VIEWER'],
  'platform-users': ['PLATFORM_ADMIN'],
  'platform-audit': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_VIEWER'],
  'platform-health': ['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_VIEWER'],
  'platform-settings': ['PLATFORM_ADMIN'],
};

export const PAGE_HEADER_TITLES: Record<string, string> = {
  overview: 'Overview',
  devices: 'Devices',
  'policies-compliance': 'Policies & Compliance',
  'workstation-policies': 'Workstation Policies',
  'mobile-devices': 'Mobile Devices',
  'server-baselines': 'Server Compliance Baselines',
  'policy-deployment-status': 'Deployment Status',
  'compliance-results': 'Compliance Results',
  'compliance-exceptions': 'Exceptions & Approvals',
  policies: 'Workstation Policies',
  'app-deployment': 'Apps & Deployment',
  'remote-execution': 'Remote Execution',
  'patch-management': 'Patch Management',
  inventory: 'Inventory',
  compliance: 'Compliance Results',
  'license-management': 'License Management',
  'asset-management': 'Asset Directory',
  'assets-dashboard': 'Asset Dashboard',
  'assets-register': 'Asset Register',
  'assets-upload': 'Asset Register',
  'assets-mapping': 'Device Mapping',
  'assets-enrollment-control': 'Enrollment Control',
  'assets-enrollment-attempts': 'Enrollment Attempts',
  'software-metering': 'Software Metering',
  'software-metering-dashboard': 'Software Metering',
  'software-metering-applications': 'Software Metering',
  'software-metering-devices': 'Software Metering',
  'software-metering-users': 'Software Metering',
  'software-metering-policy': 'Software Metering Policy',
  'software-metering-reports': 'Software Metering Reports',
  analytics: 'Analytics',
  'buntu-companion': 'Buntu Companion',
  'platform-admin': 'Platform Admin',
  'platform-dashboard': 'Platform Dashboard',
  'platform-clients': 'Clients / Tenants',
  'platform-buntu': 'Buntu Broadcasts',
  'platform-companion': 'Buntu Companion',
  'platform-subscriptions': 'Subscriptions',
  'platform-billing': 'Billing',
  'platform-license': 'License & Usage',
  'platform-entitlements': 'Module & OS Entitlements',
  'platform-software-metering': 'Software Metering',
  'platform-users': 'Platform Users',
  'platform-audit': 'Platform Audit Logs',
  'platform-health': 'System Health',
  'platform-settings': 'Platform Settings',
  'client-admin': 'Client Admin',
  'user-management': 'Client Admin',
  settings: 'Settings',
};

export const TAB_ID_ALIASES: Record<string, string> = {
  'buntu-dashboard': 'overview',
  'buntu-mdm': 'policies-compliance',
  baselines: 'policies-compliance',
  policies: 'policies-compliance',
  compliance: 'policies-compliance',
  'fleet-health': 'patch-management',
  'deployment-logs': 'patch-management',
  'application-deployment': 'app-deployment',
  'go-agents': 'inventory',
  android: 'devices',
  'custom-reports': 'analytics',
  'run-custom-script': 'remote-execution',
  'platform-admin': 'platform-clients',
  'assets-dashboard': 'asset-management',
  'assets-register': 'asset-management',
  'assets-upload': 'asset-management',
  'assets-mapping': 'asset-management',
  'assets-enrollment-control': 'asset-management',
  'assets-enrollment-attempts': 'asset-management',
  'software-metering-dashboard': 'software-metering',
  'software-metering-applications': 'software-metering',
  'software-metering-devices': 'software-metering',
  'software-metering-users': 'software-metering',
  'software-metering-policy': 'software-metering',
  'software-metering-reports': 'software-metering',
  // Deep links under Policies & Compliance stay on the single nav item
  'workstation-policies': 'policies-compliance',
  'mobile-devices': 'policies-compliance',
  'server-baselines': 'policies-compliance',
  'policy-deployment-status': 'policies-compliance',
  'compliance-results': 'policies-compliance',
  'compliance-exceptions': 'policies-compliance',
  // Users live under Client Admin (avoid duplicate top-level nav)
  'user-management': 'client-admin',
};

/** Path sync for Asset Management deep links (no React Router). */
export const ASSET_PATH_TO_VIEW: Record<string, string> = {
  '/assets': 'dashboard',
  '/assets/dashboard': 'dashboard',
  '/assets/register': 'register',
  '/assets/upload': 'register', // merged into Asset Register
  '/assets/mapping': 'mapping',
  '/assets/enrollment-control': 'enrollment-control',
  '/assets/enrollment-attempts': 'enrollment-attempts',
};

export const ASSET_VIEW_TO_PATH: Record<string, string> = {
  dashboard: '/assets/dashboard',
  register: '/assets/register',
  upload: '/assets/register',
  mapping: '/assets/mapping',
  'enrollment-control': '/assets/enrollment-control',
  'enrollment-attempts': '/assets/enrollment-attempts',
  detail: '/assets',
};

/** Path sync for Software Metering deep links (no React Router). */
export const SM_PATH_TO_VIEW: Record<string, string> = {
  '/software-metering': 'dashboard',
  '/software-metering/dashboard': 'dashboard',
  '/software-metering/applications': 'applications',
  '/software-metering/devices': 'devices',
  '/software-metering/users': 'users',
  '/software-metering/policy': 'policy',
  '/software-metering/reports': 'reports',
};

export const SM_VIEW_TO_PATH: Record<string, string> = {
  dashboard: '/software-metering/dashboard',
  applications: '/software-metering/applications',
  devices: '/software-metering/devices',
  users: '/software-metering/users',
  policy: '/software-metering/policy',
  reports: '/software-metering/reports',
};

/** Path sync for Policies & Compliance domain pages. */
export const GOVERNANCE_PATH_TO_VIEW: Record<string, string> = {
  '/policies/workstations': 'workstations',
  '/policies/mobile': 'mobile',
  '/compliance/server-baselines': 'server-baselines',
  '/policies/assignments': 'workstations', // removed tab; redirect
  '/policies/deployment-status': 'deployment-status',
  '/compliance/results': 'results',
  '/compliance/classification': 'classification',
  '/compliance/exceptions': 'exceptions',
};

export const GOVERNANCE_VIEW_TO_PATH: Record<string, string> = {
  workstations: '/policies/workstations',
  mobile: '/policies/mobile',
  'server-baselines': '/compliance/server-baselines',
  'deployment-status': '/policies/deployment-status',
  results: '/compliance/results',
  classification: '/compliance/classification',
  exceptions: '/compliance/exceptions',
};

export const GOVERNANCE_TAB_TO_VIEW: Record<string, string> = {
  'workstation-policies': 'workstations',
  'mobile-devices': 'mobile',
  'server-baselines': 'server-baselines',
  'policy-deployment-status': 'deployment-status',
  'compliance-results': 'results',
  'device-classification': 'classification',
  'compliance-exceptions': 'exceptions',
};

export function isPoliciesComplianceTab(tabId: string): boolean {
  const resolved = resolveTabId(tabId);
  return (
    resolved === 'policies-compliance' ||
    Object.keys(GOVERNANCE_TAB_TO_VIEW).includes(tabId) ||
    Object.keys(GOVERNANCE_TAB_TO_VIEW).includes(resolved)
  );
}

export function resolveTabId(tabId: string): string {
  return TAB_ID_ALIASES[tabId] ?? tabId;
}

export function isUEMNavTab(tabId: string): boolean {
  const resolved = resolveTabId(tabId);
  return (
    UEM_NAV_ITEMS.some((item) => item.id === resolved) ||
    PLATFORM_NAV_ITEMS.some((item) => item.id === resolved) ||
    POLICIES_COMPLIANCE_NAV_ITEMS.some((item) => item.id === resolved) ||
    isPoliciesComplianceTab(resolved)
  );
}

export function isPlatformNavTab(tabId: string): boolean {
  const resolved = resolveTabId(tabId);
  return PLATFORM_NAV_ITEMS.some((item) => item.id === resolved);
}

/** True when role may open this platform nav page (PLATFORM_ADMIN always). */
export function canAccessPlatformPage(tabId: string, role: string): boolean {
  const resolved = resolveTabId(tabId);
  const roleUpper = String(role || '').toUpperCase();
  if (roleUpper === 'PLATFORM_ADMIN') return true;
  const allowed = PLATFORM_PAGE_ROLES[resolved];
  if (!allowed) return isPlatformNavTab(resolved);
  return allowed.includes(roleUpper);
}
